import React, { useEffect } from 'react';
import resumeData from '../data/resumeData';

function Resume() {
  useEffect(() => {
    document.title = 'Sydney Ferris - Resume';
  }, []);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen theme-bg-secondary p-4 sm:p-6 md:p-8 font-Nunito">
      <div className="max-w-4xl mx-auto theme-bg shadow-lg rounded-lg p-4 sm:p-6 md:p-8">
        {/* Header */}
        <div className="border-b-2 theme-border pb-4 sm:pb-6 mb-4 sm:mb-6">
          <h1 className="text-3xl sm:text-4xl font-bold theme-primary mb-2 font-Marcellus tracking-[.2em] uppercase text-center">{resumeData.name}</h1>
          <p className="text-lg sm:text-xl theme-secondary mb-4 text-center">{resumeData.title}</p>
          <div className="flex flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm theme-primary justify-center">
            <a href={resumeData.contact.website} target="_blank" rel="noopener noreferrer" className="hover:underline">🌐 {resumeData.contact.website}</a>
            <a href={resumeData.contact.linkedin} target="_blank" rel="noopener noreferrer" className="hover:underline">🔗 {resumeData.contact.linkedin}</a>
            <a href={resumeData.contact.github} target="_blank" rel="noopener noreferrer" className="hover:underline">💻 {resumeData.contact.github}</a>
          </div>
          {/* <button
            onClick={handlePrint}
            className="rounded-md tracking-widest theme-bg-accent px-3.5 py-2.5 text-center text-sm font-semibold theme-accent-contrast shadow-sm hover:opacity-80"
          >
            Print to PDF
          </button> */}
        </div>

        {/* Summary */}
        <div className="mb-6 sm:mb-8">
          <h2 className="text-xl sm:text-2xl font-bold theme-primary mb-3 border-b theme-border pb-2">Summary</h2>
          <p className="theme-primary leading-relaxed text-sm sm:text-base">{resumeData.summary}</p>
        </div>

        {/* Experience */}
        <div className="mb-6 sm:mb-8">
          <h2 className="text-xl sm:text-2xl font-bold theme-primary mb-3 border-b theme-border pb-2">Experience</h2>
          {resumeData.experience.map((exp, index) => (
            <div key={index} className="mb-4 sm:mb-6">
              <div className="flex flex-col sm:flex-row justify-between items-start mb-2 gap-1">
                <div>
                  <h3 className="text-lg sm:text-xl font-semibold theme-primary">{exp.title}</h3>
                  <p className="theme-primary text-sm sm:text-base">{exp.company} • {exp.location}</p>
                </div>
                <span className="theme-secondary text-xs sm:text-sm">
                  {exp.startDate} – {exp.endDate}
                </span>
              </div>
              <ul className="list-disc list-inside theme-primary ml-4 text-sm sm:text-base">
                {exp.description.map((item, i) => (
                  <li key={i} className="mb-1">{item}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Education */}
        <div className="mb-6 sm:mb-8">
          <h2 className="text-xl sm:text-2xl font-bold theme-primary mb-3 border-b theme-border pb-2">Education</h2>
          {resumeData.education.map((edu, index) => (
            <div key={index} className="mb-4">
              <div className="flex flex-col sm:flex-row justify-between items-start gap-1">
                <div>
                  <h3 className="text-lg sm:text-xl font-semibold theme-primary">{edu.degree}</h3>
                  <p className="theme-primary text-sm sm:text-base">{edu.school} • {edu.location}</p>
                </div>
                <span className="theme-secondary text-xs sm:text-sm">
                  {edu.startDate} – {edu.endDate}
                </span>
              </div>
              <div className="mt-2 text-xs sm:text-sm theme-primary">
                {edu.gpa && <span className="mr-4">GPA: {edu.gpa}</span>}
                {edu.honors && <span className="font-semibold theme-primary">{edu.honors}</span>}
              </div>
              {edu.courses && (
                <div className="mt-3">
                  <p className="text-xs sm:text-sm font-semibold theme-primary mb-2">Relevant Coursework:</p>
                  <div className="flex flex-wrap gap-2">
                    {edu.courses.map((course, i) => (
                      <span
                        key={i}
                        className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs"
                      >
                        {course}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Skills */}
        <div className="mb-6 sm:mb-8">
          <h2 className="text-xl sm:text-2xl font-bold theme-primary mb-3 border-b theme-border pb-2">Skills</h2>
          <div className="flex flex-wrap gap-2">
            {resumeData.skills.map((skill, index) => (
              <span
                key={index}
                className="theme-bg-secondary theme-primary px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm"
              >
                {skill}
              </span>
            ))}
          </div>
        </div>

        {/* Projects */}
        <div>
          <h2 className="text-xl sm:text-2xl font-bold theme-primary mb-3 border-b theme-border pb-2">Projects</h2>
          {resumeData.projects.map((project, index) => (
            <div key={index} className="mb-4">
              {project.link ? (
                <a 
                  href={project.link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-lg sm:text-xl font-bold theme-primary hover:underline cursor-pointer"
                >
                  {project.name}
                </a>
              ) : (
                <h3 className="text-lg sm:text-xl font-semibold theme-primary">{project.name}</h3>
              )}
              <div className="theme-primary mb-2 text-sm sm:text-base">
                {Array.isArray(project.description) ? (
                  <ul className="list-disc list-inside ml-4">
                    {project.description.map((item, i) => (
                      <li key={i} className="mb-1">{item}</li>
                    ))}
                  </ul>
                ) : (
                  <p>{project.description}</p>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {project.technologies.map((tech, i) => (
                  <span
                    key={i}
                    className="theme-bg-secondary theme-primary px-2 py-1 rounded text-xs"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .max-w-4xl, .max-w-4xl * {
            visibility: visible;
          }
          .max-w-4xl {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            margin: 0;
            padding: 0;
          }
          .no-print {
            display: none !important;
          }
          .theme-bg-secondary {
            background: white !important;
          }
          .theme-bg {
            background: white !important;
            box-shadow: none !important;
            border-radius: 0 !important;
          }
          @page {
            margin: 0.5cm;
          }
        }
      `}</style>
    </div>
  );
}

export default Resume;
